import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Form } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';
import { cloneDeep } from 'lodash';

import { CommonImageItemType, applyContainerImageItemToForm, createSystemImageFieldMappings } from '@/components-biz/ImageSelect';
import { MAAS_INFERENCE_SOURCE_FIELD, MaasFramework, MaasInferenceSource } from '@/delivery-type-plugin/capabilities/maas-init';
import {
  IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD,
  applyImageContainerDeployConfigToForm,
  buildImageItemFromInferenceSystem,
  clearImageContainerDeployConfigFromForm,
  parseFormJsonField,
  parseInferenceFrameworkImageItem,
  resolveFrameworkFromInferenceImage,
  resolveInferenceSourceFromSystem,
  resolvePresetInferenceImageItem,
  syncBuiltinInferenceImageEchoFields,
} from '@/delivery-type-plugin/plugins';

import {
  applyImageVolumesToAppStorageGroups,
  filterUserCustomStorageGroups,
  readAppStorageGroupsFromForm,
} from '../headless/appStorageGroups';
import type { ApplicationInstanceFormValues } from '../types';

interface ApplicationImageSnapshot {
  inferenceSource: MaasInferenceSource;
  system?: Record<string, unknown>;
  framework?: MaasFramework;
  imageContainerDeployInstanceScheduling?: ApplicationInstanceFormValues['_imageContainerDeployInstanceScheduling'];
}

interface UseApplicationImageSettingOptions {
  form: FormInstance;
  isEdit?: boolean;
  initValues?: ApplicationInstanceFormValues;
  systemField: string;
  systemDiskField: string;
  presetArchitecture?: string;
  presetImageLocked?: boolean;
}

/** 应用镜像联动逻辑：负责来源切换、编辑态快照恢复、镜像默认值写入。 */
export function useApplicationImageSetting({
  form,
  isEdit,
  initValues,
  systemField,
  systemDiskField: _systemDiskField,
  presetArchitecture: _presetArchitecture,
  presetImageLocked,
}: UseApplicationImageSettingOptions) {
  const appSystemRaw = Form.useWatch(systemField, form);
  const inferenceFrameworkImageRaw = Form.useWatch('inferenceFrameworkImage', form);
  const appSystem = parseFormJsonField<Record<string, unknown>>(appSystemRaw);
  const imageMappings = useMemo(
    () => createSystemImageFieldMappings(systemField).filter((item) => !item.field.endsWith('.architecture')),
    [systemField],
  );

  const initialSnapshot = useMemo<ApplicationImageSnapshot | undefined>(() => {
    if (!isEdit || !initValues) return undefined;
    const system = parseFormJsonField<Record<string, unknown>>(initValues._inferenceSystem);
    if (!system) return undefined;
    const imageUrl = String(system.image || system.url || system.imageKey || '').trim();
    if (!imageUrl) return undefined;
    return {
      inferenceSource: resolveInferenceSourceFromSystem(system),
      system: cloneDeep(system),
      framework: initValues.framework,
      imageContainerDeployInstanceScheduling: initValues._imageContainerDeployInstanceScheduling
        ? cloneDeep(initValues._imageContainerDeployInstanceScheduling)
        : undefined,
    };
  }, [initValues, isEdit]);

  const readImageSourceFromForm = useCallback((): MaasInferenceSource => {
    const saved = form.getFieldValue(MAAS_INFERENCE_SOURCE_FIELD) as MaasInferenceSource | undefined;
    if (saved === MaasInferenceSource.Builtin || saved === MaasInferenceSource.Custom) {
      return saved;
    }
    return resolveInferenceSourceFromSystem(parseFormJsonField<Record<string, unknown>>(form.getFieldValue(systemField)));
  }, [form, systemField]);

  const [imageSource, setImageSource] = useState<MaasInferenceSource>(() => readImageSourceFromForm());
  const sourceChangeLockRef = useRef(false);
  /** 制品/广场跳转直建：第二步才挂载镜像组件，需在挂载后补写扁平 imageKey（对齐无 SKU） */
  const createPresetAppliedRef = useRef(false);

  const presetImageItem = useMemo(() => {
    if (!presetImageLocked) return null;
    return resolvePresetInferenceImageItem({
      inferenceFrameworkImage: inferenceFrameworkImageRaw,
      _inferenceSystem: appSystemRaw,
    });
  }, [appSystemRaw, inferenceFrameworkImageRaw, presetImageLocked]);

  /** 创建态：第二步挂载后补写扁平 imageKey；已有选中则只做 echo，避免覆盖用户改动 */
  useEffect(() => {
    if (isEdit || presetImageLocked || createPresetAppliedRef.current) return;

    const currentKey = String(form.getFieldValue(`${systemField}.imageKey`) ?? '').trim();
    const currentName = String(form.getFieldValue(`${systemField}.imageName`) ?? '').trim();
    if (currentKey || currentName) {
      createPresetAppliedRef.current = true;
      const system = parseFormJsonField<Record<string, unknown>>(form.getFieldValue(systemField));
      if (system) {
        form.setFieldValue(MAAS_INFERENCE_SOURCE_FIELD, MaasInferenceSource.Builtin);
        setImageSource(MaasInferenceSource.Builtin);
        syncBuiltinInferenceImageEchoFields(form, systemField, system, form.getFieldValue('inferenceFrameworkImage'));
      }
      return;
    }

    const systemFromInit = parseFormJsonField<Record<string, unknown>>(initValues?._inferenceSystem);
    const systemFromForm = parseFormJsonField<Record<string, unknown>>(form.getFieldValue(systemField));
    const system = systemFromForm || systemFromInit;
    const catalogItem =
      parseInferenceFrameworkImageItem(form.getFieldValue('inferenceFrameworkImage')) ||
      parseInferenceFrameworkImageItem(initValues?.inferenceFrameworkImage) ||
      (system ? buildImageItemFromInferenceSystem(system) : null);

    if (!catalogItem && !system) return;

    const nextId = String(catalogItem?.id ?? catalogItem?.key ?? system?.imageKey ?? '').trim();
    const nextName = String(catalogItem?.name ?? system?.imageName ?? '').trim();
    const nextUrl = String(catalogItem?.dockerRefUrl ?? system?.image ?? system?.url ?? '').trim();
    if (!nextId && !nextName && !nextUrl) return;

    createPresetAppliedRef.current = true;
    sourceChangeLockRef.current = true;

    form.setFieldValue(MAAS_INFERENCE_SOURCE_FIELD, MaasInferenceSource.Builtin);
    setImageSource(MaasInferenceSource.Builtin);

    if (catalogItem) {
      const normalized = { ...catalogItem, key: catalogItem.id ?? catalogItem.key };
      const userGroups = filterUserCustomStorageGroups(readAppStorageGroupsFromForm(form));
      form.setFieldValue('inferenceFrameworkImage', JSON.stringify(normalized));
      if (nextId) form.setFieldValue(`${systemField}.imageKey`, nextId);
      applyContainerImageItemToForm(form, normalized, imageMappings);
      if (nextUrl) form.setFieldValue(`${systemField}.image`, nextUrl);
      if (nextName) form.setFieldValue(`${systemField}.imageName`, nextName);
      applyImageContainerDeployConfigToForm(form, normalized);
      applyImageVolumesToAppStorageGroups(form, normalized, userGroups);
      form.setFieldValue('framework', resolveFrameworkFromInferenceImage(normalized, form.getFieldValue(systemField)));
    } else if (system) {
      form.setFieldValue(systemField, system);
      if (nextId) form.setFieldValue(`${systemField}.imageKey`, nextId);
      if (nextName) form.setFieldValue(`${systemField}.imageName`, nextName);
      if (nextUrl) {
        form.setFieldValue(`${systemField}.image`, nextUrl);
        form.setFieldValue(`${systemField}.url`, nextUrl);
      }
      syncBuiltinInferenceImageEchoFields(form, systemField, system, form.getFieldValue('inferenceFrameworkImage'));
    }

    window.setTimeout(() => {
      sourceChangeLockRef.current = false;
    }, 0);
  }, [form, imageMappings, initValues, isEdit, presetImageLocked, systemField]);

  /** 编辑旧 preset 常缺 maasInferenceSource；本地 state 能推导但 Form 字段为空会导致确认步「必填」失败 */
  useEffect(() => {
    if (presetImageLocked) return;
    if (sourceChangeLockRef.current) return;

    const saved = form.getFieldValue(MAAS_INFERENCE_SOURCE_FIELD) as MaasInferenceSource | undefined;
    if (saved === MaasInferenceSource.Builtin || saved === MaasInferenceSource.Custom) {
      setImageSource(saved);
      if (saved === MaasInferenceSource.Builtin && appSystem) {
        syncBuiltinInferenceImageEchoFields(form, systemField, appSystem, inferenceFrameworkImageRaw);
      }
      return;
    }

    const resolved = resolveInferenceSourceFromSystem(appSystem);
    setImageSource(resolved);
    form.setFieldValue(MAAS_INFERENCE_SOURCE_FIELD, resolved);

    if (resolved === MaasInferenceSource.Builtin && appSystem) {
      syncBuiltinInferenceImageEchoFields(form, systemField, appSystem, inferenceFrameworkImageRaw);
    } else if (resolved === MaasInferenceSource.Custom && appSystem) {
      const imageUrl = String(appSystem.image || appSystem.url || '').trim();
      if (imageUrl && !String(form.getFieldValue(`${systemField}.image`) ?? '').trim()) {
        form.setFieldValue(`${systemField}.image`, imageUrl);
      }
    }
  }, [appSystem, form, inferenceFrameworkImageRaw, presetImageLocked, systemField]);

  const handleSourceChange = useCallback(
    (source: MaasInferenceSource) => {
      if (presetImageLocked) return;
      sourceChangeLockRef.current = true;
      setImageSource(source);
      form.setFieldValue(MAAS_INFERENCE_SOURCE_FIELD, source);

      if (isEdit && initialSnapshot && source === initialSnapshot.inferenceSource && initialSnapshot.system) {
        form.setFieldsValue({
          [systemField]: cloneDeep(initialSnapshot.system),
          framework: initialSnapshot.framework,
        });
        if (initialSnapshot.imageContainerDeployInstanceScheduling) {
          form.setFieldValue(
            IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD,
            cloneDeep(initialSnapshot.imageContainerDeployInstanceScheduling),
          );
        }
      } else {
        form.setFieldsValue({
          [systemField]: undefined,
          [`${systemField}.imageKey`]: undefined,
          [`${systemField}.imageName`]: undefined,
          framework: undefined,
          inferenceFrameworkImage: undefined,
        });
        clearImageContainerDeployConfigFromForm(form);
        const userGroups = filterUserCustomStorageGroups(readAppStorageGroupsFromForm(form));
        applyImageVolumesToAppStorageGroups(form, null, userGroups);
      }

      requestAnimationFrame(() => {
        sourceChangeLockRef.current = false;
      });
    },
    [form, initialSnapshot, isEdit, presetImageLocked, systemField],
  );

  const clearApplicationImageFields = useCallback(() => {
    imageMappings.forEach(({ field }) => {
      form.setFieldValue(field, undefined);
    });
    form.setFieldsValue({
      [`${systemField}.imageKey`]: undefined,
      [`${systemField}.imageName`]: undefined,
      framework: undefined,
      inferenceFrameworkImage: undefined,
    });
    clearImageContainerDeployConfigFromForm(form);
    const userGroups = filterUserCustomStorageGroups(readAppStorageGroupsFromForm(form));
    applyImageVolumesToAppStorageGroups(form, null, userGroups);
  }, [form, imageMappings, systemField]);

  const handleImageChange = useCallback(
    async (_key: string, item: CommonImageItemType, imageUrl?: string) => {
      if (presetImageLocked) return;
      const catalogItem = { ...item, key: item.id ?? item.key };
      const userGroups = filterUserCustomStorageGroups(readAppStorageGroupsFromForm(form));
      const current = parseFormJsonField<CommonImageItemType>(form.getFieldValue('inferenceFrameworkImage'));
      const currentId = String(current?.id ?? current?.key ?? '').trim();
      const nextId = String(catalogItem.id ?? catalogItem.key ?? '').trim();

      clearImageContainerDeployConfigFromForm(form);
      form.setFieldValue('inferenceFrameworkImage', JSON.stringify(catalogItem));
      form.setFieldValue(`${systemField}.imageKey`, nextId);
      applyContainerImageItemToForm(form, catalogItem, imageMappings);
      const resolvedUrl = imageUrl ?? String(catalogItem.dockerRefUrl ?? '').trim();
      if (resolvedUrl) {
        form.setFieldValue(`${systemField}.image`, resolvedUrl);
      }

      applyImageContainerDeployConfigToForm(form, catalogItem);

      if (!currentId || currentId !== nextId) {
        applyImageVolumesToAppStorageGroups(form, catalogItem, userGroups);
      }
      form.setFieldValue('framework', resolveFrameworkFromInferenceImage(catalogItem, form.getFieldValue(systemField)));
    },
    [form, imageMappings, presetImageLocked, systemField],
  );

  const handleImageClear = useCallback(() => {
    if (presetImageLocked) return;
    clearApplicationImageFields();
  }, [clearApplicationImageFields, presetImageLocked]);

  return {
    imageMappings,
    imageSource,
    handleSourceChange,
    handleImageChange,
    handleImageClear,
    presetImageItem,
  };
}
